import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Modal } from 'antd';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { AIRFLOW_BASE_PATH, DAG_DETAIL_TAB, DagDetailTab, MODEL_REQUEST_DAG_PREFIX } from '../constants';
import { AirflowDag, AirflowDagDetails, AirflowDagRun } from '../types';

/*
 * Manages the DAG detail screen's header data, active tab, refresh propagation, and the
 * pause/trigger/delete actions shared by the header across every tab
 */
export function useDagDetail() {
    // react-router-dom v5's useParams does not decode dynamic segments — dagId arrives exactly
    // as it appeared in the URL (still percent-encoded), so encoding it again for an API call
    // would double-encode. Decode once here, right at the source.
    const { dagId: rawDagId } = useParams<{ dagId: string }>();
    const dagId = decodeURIComponent(rawDagId);
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const history = useHistory();
    const location = useLocation();

    const [dag, setDag] = useState<AirflowDag | null>(null);
    const [details, setDetails] = useState<AirflowDagDetails | null>(null);
    const [latestRun, setLatestRun] = useState<AirflowDagRun | null>(null);
    const [isLoadingDag, setIsLoadingDag] = useState(true);
    const [togglingPause, setTogglingPause] = useState(false);
    const [triggering, setTriggering] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const activeTab = useMemo<DagDetailTab>(() => {
        const params = new URLSearchParams(location.search);
        return (params.get('tab') as DagDetailTab) || DAG_DETAIL_TAB.overview;
    }, [location.search]);

    /*
     * Updates the active tab, reflected in the ?tab= query param
     */
    const handleTabChange = useCallback(
        (tab: string) => {
            const params = new URLSearchParams(location.search);
            params.set('tab', tab);
            history.replace({ pathname: location.pathname, search: params.toString() });
        },
        [history, location.pathname, location.search]
    );

    /*
     * Fetches the DAG summary, details, and latest run in parallel
     */
    const fetchAll = useCallback(async () => {
        try {
            const [dagResponse, detailsResponse, runsResponse] = await Promise.all([
                AirflowDashboardHandler.getDag(Env, dagId),
                AirflowDashboardHandler.getDagDetails(Env, dagId),
                AirflowDashboardHandler.listDagRuns(Env, dagId, { limit: 1, order_by: '-execution_date' }),
            ]);
            setDag(dagResponse);
            setDetails(detailsResponse);
            setLatestRun(runsResponse.dag_runs[0] ?? null);
        } catch {
            errorAlert(`Failed to load DAG "${dagId}".`);
        } finally {
            setIsLoadingDag(false);
        }
    }, [Env, dagId]);

    useEffect(() => {
        fetchAll();
    }, [dagId]);

    /*
     * Re-fetches header data and bumps the refresh key that every tab watches
     */
    const handleRefresh = useCallback(() => {
        fetchAll();
        setRefreshKey((key) => key + 1);
    }, [fetchAll]);

    /*
     * Toggles the DAG's paused state optimistically
     */
    const handleTogglePause = useCallback(async () => {
        if (!dag) return;
        setTogglingPause(true);
        try {
            await AirflowDashboardHandler.patchDag(Env, dagId, { is_paused: !dag.is_paused });
            setDag((prev) => (prev ? { ...prev, is_paused: !prev.is_paused } : prev));
            successAlert(`DAG "${dagId}" ${dag.is_paused ? 'activated' : 'paused'}.`);
        } catch {
            errorAlert(`Failed to update DAG "${dagId}".`);
        } finally {
            setTogglingPause(false);
        }
    }, [Env, dag, dagId]);

    /*
     * Opens the Trigger DAG dialog
     */
    const handleTrigger = useCallback(() => {
        setTriggering(true);
    }, []);

    /*
     * Closes the Trigger DAG dialog and refreshes header + tab data
     */
    const handleTriggerDialogClose = useCallback(() => {
        setTriggering(false);
        handleRefresh();
    }, [handleRefresh]);

    /*
     * Blocks deletion of PI-integrator-managed DAGs, otherwise confirms and deletes,
     * navigating back to the DAGs list on success
     */
    const handleDeleteRequest = useCallback(() => {
        if (dagId.startsWith(MODEL_REQUEST_DAG_PREFIX)) {
            errorAlert('This DAG is associated with PI-integrator, so deletion is not allowed.');
            return;
        }
        Modal.confirm({
            className: 'airflowDashboardRoot',
            title: 'Delete DAG?',
            content: `Are you sure you want to delete DAG "${dagId}"?`,
            okText: 'Yes, Delete',
            cancelText: 'Cancel',
            okButtonProps: { style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' } },
            onOk: async () => {
                try {
                    await AirflowDashboardHandler.deleteDag(Env, dagId);
                    successAlert(`Deleted DAG "${dagId}". It may take up to 30 seconds to reflect.`);
                    history.push(AIRFLOW_BASE_PATH);
                } catch {
                    errorAlert('Error deleting DAG');
                }
            },
        });
    }, [Env, dagId, history]);

    /*
     * Navigates back to the DAGs list
     */
    const handleBack = useCallback(() => {
        history.push(AIRFLOW_BASE_PATH);
    }, [history]);

    return {
        dagId,
        dag,
        details,
        latestRun,
        isLoadingDag,
        togglingPause,
        triggering,
        refreshKey,
        activeTab,
        handleTabChange,
        handleRefresh,
        handleTogglePause,
        handleTrigger,
        handleTriggerDialogClose,
        handleDeleteRequest,
        handleBack,
    };
}
